import React from 'react';
import classnames from 'classnames';
import throttle from 'lodash.throttle';

import type { Repub } from '@cx-modules/constants';

import type INavData from '../../interfaces/config/nav';
import type INavigationLink from '../../interfaces/navigation-link';
import useStore from '../../store';
import {
  getInternetExplorerVersion,
  pageScroll,
  scrollTo } from
'../../util/utils';

import DesktopHeader from './components/desktop-header';
import MobileHeader from './components/mobile-header';
import styles from './header.module.css';

interface IProps {
  isRebrandEnabled: boolean;
  navData: INavData;
  repub: Repub;
  resourcesUrl: string;
  subMenu: INavigationLink[];
}
enum ScrollDirection {
  up,
  down,
}

let lastPosition: number = 0;

const debounceWrap = (fn: any) => throttle(fn, 16, { leading: true });

const Header: React.FC<IProps> = ({
  isRebrandEnabled,
  navData,
  repub,
  resourcesUrl,
  subMenu
}) => {
  const [visible, setVisible] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [sticky, setSticky] = React.useState(false);
  const [popState, setPopState] = React.useState(false);
  const [hasEventMenu, setHasEventMenu] = React.useState(false);
  const [headerVisibility, setHeaderVisibility] = React.useState<string>('');

  const currentPath = useStore((state) => state.config.currentPath);
  const navDrawerOpen = useStore((state) => state.navDrawerOpen);
  const navigation = useStore((state) => state.navigation);
  const setVisibleHeader = useStore((state) => state.setVisibleHeader);
  const setPageHasThirdLevelNav = useStore(
    (state) => state.setPageHasThirdLevelNav
  );
  const [showAllSports, setShowAllSports] = useStore(
    ({ setShowAllSports, showAllSports }) => [showAllSports, setShowAllSports]
  );
  const [headerHeight, setHeaderHeight] = useStore(
    ({ headerHeight, setHeaderHeight }) => [headerHeight, setHeaderHeight]
  );

  React.useEffect(() => {
    setHasEventMenu(navData.eventMenu?.select?.menu?.length > 0);
  }, [navData]);

  const handleScrollToHash = () => {
    if (window.location.hash) {
      setTimeout(() => {
        const hashElement = document.querySelector(window.location.hash);
        scrollTo(hashElement);
      }, 500);
    }
  };

  const handleScrollToTopIfNoHashElement = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        // NOTE: browser will natively scroll for hash elements
        const hashElement = document.querySelector(window.location.hash);
        if (hashElement) return;
      } catch (error) {

        // Quick fix - Glen will address
      }
      const bodyElement = document.querySelector('#body');
      if (popState) {
        scrollTo(bodyElement, 16);
        setPopState(false);
      }
    }
  }, [popState]);

  const calculateHeaderHeight = React.useCallback(() => {
    const headerAndCookie = document.getElementById('headerAndCookie');
    if (headerAndCookie) {
      const headerHeight =
      headerAndCookie?.getBoundingClientRect()?.height || 0;
      setHeaderHeight(headerHeight);

      // Detect if we have a thirdLevelNav on the page
      const pageHasThirdLevelNav = !!subMenu?.length;
      setPageHasThirdLevelNav(pageHasThirdLevelNav);
    }
  }, [subMenu]);

  const triggerHeaderToggle = (state: boolean): void => {
    // trigger header events for any component that listens to it
    const event = new CustomEvent('OC:HeaderToggle', { detail: state });
    window.dispatchEvent(event);
  };

  const getThreshold = React.useCallback(() => {
    const headerWrapperEl = document.getElementById('headerWrapper');
    const headerTopOffset =
    headerWrapperEl?.parentElement?.getBoundingClientRect().top || 0;

    // I dislike magic numbers but this one works to set the threshold just enough to let the event menu show up
    const originalMagic =
    Math.max(headerTopOffset, 0) + (
    hasEventMenu ? headerHeight * 0.6 : headerHeight);

    // I dont know if this breaks magic for the event menu to show up
    const result = Math.max(originalMagic, headerWrapperEl?.offsetHeight || 0);

    return result;
  }, [headerHeight]);

  const handlePopState = () => setPopState(true);

  const stickyHeader = React.useCallback(
    (direction: ScrollDirection) => {
      // Do nothing if nav drawer is open
      if (navDrawerOpen) {
        return;
      }

      const top =
      document.
      getElementById('headerWrapper')?.
      parentElement?.getBoundingClientRect().top || 0;

      if (direction === ScrollDirection.up) {
        // trigger header events for any component that listens to it
        triggerHeaderToggle(true);

        setVisible(true);
        setScrolled(top < 0);
        setVisibleHeader(true);
      } else {
        // Resets popstate on scroll down
        if (popState) {
          setPopState(false);
        }

        const threshold = getThreshold();
        const currentScrollAmount = pageScroll();

        // cases where there is something above the body (e.g. gdpr widget)
        if (currentScrollAmount > threshold) {
          triggerHeaderToggle(false);

          setVisible(false);
          setSticky(true);

          setTimeout(() => {
            setScrolled(true);
          }, 0);

          setVisibleHeader(false);
          setShowAllSports(false);
        }
      }
    },
    [calculateHeaderHeight, getThreshold, navDrawerOpen, popState]
  );

  const handleScroll = () => {
    if (getInternetExplorerVersion() == 11) {
      return;
    }

    calculateHeaderHeight();

    if (showAllSports) {
      return;
    }

    const pos = pageScroll();

    const direction =
    lastPosition > pos ? ScrollDirection.up : ScrollDirection.down;

    if (lastPosition !== pos) {
      lastPosition = pos;
      stickyHeader(direction);
    }
  };

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', debounceWrap(handleScroll), {
        capture: false,
        passive: true
      });

      // Event listener for browser back and forward actions
      window.addEventListener('popstate', handlePopState, {
        capture: false,
        passive: true
      });
    }

    setVisibleHeader(true);
    calculateHeaderHeight();
    window.addEventListener('load', handleScrollToHash);

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', debounceWrap(handleScroll));
        window.removeEventListener('popstate', handlePopState);
      }
    };
  }, []);

  React.useEffect(() => {
    const hasNewEventMenu = navData.eventMenu?.select?.menu?.length > 0;
    if (hasNewEventMenu !== hasEventMenu) {
      setHasEventMenu(hasNewEventMenu);
      stickyHeader(ScrollDirection.up);
    } else {
      stickyHeader(ScrollDirection.up);
    }
    // Scroll to top of page if no hash element on popState
    if (popState) {
      handleScrollToTopIfNoHashElement();
    }
  }, [navData, popState, hasEventMenu]);

  const renderHeader = (): JSX.Element => {
    const {
      allSports,
      featuredMenu,
      headerMenu: { featuredSports, pages }
    } = navData;

    // appropriate header display controlled by css
    return (
      <>
				<DesktopHeader
          isRebrandEnabled={isRebrandEnabled}
          pages={pages}
          featuredSports={featuredSports}
          featuredMenu={featuredMenu}
          allSports={allSports}
          subMenu={subMenu}
          resourcesUrl={resourcesUrl}
          repub={repub} />

				<MobileHeader
          isRebrandEnabled={isRebrandEnabled}
          path={currentPath}
          subMenu={subMenu}
          pages={pages}
          resourcesUrl={resourcesUrl}
          repub={repub} />

			</>);

  };

  React.useEffect(() => {
    if (!scrolled) {
      setHeaderVisibility('fixed');
    }

    if (sticky) {
      setHeaderVisibility('hidden');
    }

    if (visible) {
      setHeaderVisibility('visible');
    }
  }, [scrolled, sticky, visible]);

  return (
    <div
      id="headerWrapper"
      data-testid="header"
      className={classnames(
        styles.headerWrapper,
        subMenu?.length ? styles.withEventMenu : ''
      )}>

			<div
        id="stickyWrapper"
        style={{ top: '0px' }}
        className={classnames(
          styles.stickyWrapper,
          styles[headerVisibility],
          navigation.primary || navigation.secondary || showAllSports ?
          styles.noStickyAnimation :
          '',
          !scrolled && !visible ? styles.noAnimation : styles.scrolled
        )}>

				<header id="headerAndCookie">{renderHeader()}</header>
			</div>
		</div>);

};

export default Header;